import { useEffect, useRef, useState } from "react";
import { Link, useLocation, useNavigate, useSearchParams } from "react-router-dom";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardHeader } from "@/components/ui/card";
import { Alert, AlertDescription } from "@/components/ui/alert";
import {
    Loader2,
    MailCheck,
    MessageCircleQuestion,
    CheckCircle2,
    AlertTriangle,
} from "lucide-react";
import { toast } from "sonner";
import { useAuth } from "@/context/AuthContext";
import { apiError } from "@/lib/api";

/**
 * /verifica-email
 * - Se arriva con ?token=xxx → verifica automaticamente e reindirizza.
 * - Se arriva post-registrazione (state) → mostra istruzioni "check your inbox".
 */
export default function VerifyEmail() {
    const { verifyEmail, resendVerification } = useAuth();
    const navigate = useNavigate();
    const [params] = useSearchParams();
    const { state } = useLocation();
    const token = params.get("token");
    const email = state?.email || "";
    const isFresh = Boolean(state?.fresh);
    const emailSent = state?.email_sent !== false;
    const fallbackUrl = state?.verification_url;

    const [status, setStatus] = useState(token ? "verifying" : "pending");
    const [error, setError] = useState("");
    const [resending, setResending] = useState(false);
    const [resendEmail, setResendEmail] = useState(email);
    const [resentAt, setResentAt] = useState(null);
    const [fallbackUrlState, setFallbackUrlState] = useState(fallbackUrl || null);
    // Prevents duplicate verify calls from:
    //  - React 18 StrictMode double-invoke in development
    //  - Component re-mounts triggered by parent tree changes
    // The backend has its own 60-min idempotency grace window, but this guard
    // avoids an unnecessary POST and any flicker of the "error" state.
    const startedRef = useRef(false);

    useEffect(() => {
        if (!token) return;
        if (startedRef.current) return;
        startedRef.current = true;
        (async () => {
            try {
                await verifyEmail(token);
                setStatus("success");
                toast.success("Email verificata! Benvenuto in CondominioNow.");
                // Nessun redirect automatico: mostriamo la schermata di onboarding
                // così l'utente sceglie il primo passo da fare.
            } catch (err) {
                setStatus("error");
                setError(apiError(err));
            }
        })();
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [token]);

    const handleResend = async () => {
        if (!resendEmail) {
            toast.error("Inserisci l'email");
            return;
        }
        setResending(true);
        try {
            const data = await resendVerification(resendEmail);
            setResentAt(new Date());
            if (data?.verification_url) {
                setFallbackUrlState(data.verification_url);
                toast.warning("Email non consegnata, usa il link qui sotto.");
            } else {
                toast.success("Se l'account esiste, riceverai una nuova email.");
            }
        } catch (err) {
            toast.error(apiError(err));
        } finally {
            setResending(false);
        }
    };

    return (
        <div className="min-h-[calc(100vh-4rem)] flex items-start justify-center bg-slate-50 px-4 py-14 sm:py-20">
            <div className="w-full max-w-lg">
                <div className="text-center mb-8">
                    <Link to="/" className="inline-flex items-center hover:opacity-80">
                        <img src="/brand-header.png" alt="CondominioNow" className="h-[60px] w-auto object-contain" />
                    </Link>
                </div>

                {status === "verifying" && (
                    <Card className="border-slate-200 shadow-sm" data-testid="verify-loading">
                        <CardContent className="py-14 text-center">
                            <Loader2 className="size-10 mx-auto animate-spin text-primary" />
                            <h1 className="mt-5 font-serif text-2xl font-semibold">
                                Verifica in corso…
                            </h1>
                            <p className="mt-2 text-muted-foreground">
                                Stiamo confermando la tua email.
                            </p>
                        </CardContent>
                    </Card>
                )}

                {status === "success" && (
                    <Card
                        className="border-emerald-200 bg-white shadow-sm overflow-hidden"
                        data-testid="verify-success"
                    >
                        {/* Success banner */}
                        <div className="bg-emerald-50 border-b border-emerald-100 px-6 sm:px-8 py-7 text-center">
                            <CheckCircle2 className="size-12 mx-auto text-emerald-600" />
                            <h1 className="mt-4 font-serif text-2xl sm:text-3xl font-semibold text-emerald-900">
                                Email verificata con successo
                            </h1>
                            <p className="mt-2 text-emerald-800/90 text-base">
                                Il tuo account è attivo e puoi iniziare a utilizzare
                                CondominioNow.
                            </p>
                        </div>

                        {/* Onboarding checklist */}
                        <CardContent className="px-6 sm:px-8 py-7">
                            <div className="text-[11px] font-semibold uppercase tracking-[0.18em] text-primary">
                                Prossimi passi
                            </div>
                            <ul className="mt-4 space-y-3" data-testid="verify-onboarding-list">
                                {[
                                    "Completa il tuo profilo",
                                    "Fai la tua prima domanda",
                                    "Esplora le FAQ",
                                    "Consulta le guide disponibili",
                                ].map((step, i) => (
                                    <li
                                        key={i}
                                        className="flex items-center gap-3 text-slate-800"
                                    >
                                        <span
                                            aria-hidden
                                            className="inline-flex size-5 rounded-md border-2 border-slate-300 bg-white shrink-0"
                                        />
                                        <span className="text-[15px]">{step}</span>
                                    </li>
                                ))}
                            </ul>

                            {/* CTA buttons */}
                            <div className="mt-7 grid grid-cols-1 sm:grid-cols-3 gap-3">
                                <Button
                                    asChild
                                    className="h-11 rounded-full bg-primary hover:bg-primary/90 text-white font-semibold"
                                    data-testid="verify-cta-dashboard"
                                >
                                    <Link to="/dashboard">Accedi ora</Link>
                                </Button>
                                <Button
                                    asChild
                                    variant="outline"
                                    className="h-11 rounded-full border-2 border-primary text-primary hover:bg-primary hover:text-white font-semibold"
                                    data-testid="verify-cta-faq"
                                >
                                    <Link to="/faq">Vai alle FAQ</Link>
                                </Button>
                                <Button
                                    asChild
                                    className="h-11 rounded-full bg-accent hover:bg-[#E17F2C] text-white font-semibold"
                                    data-testid="verify-cta-question"
                                >
                                    <Link to="/dashboard/nuova">Fai una domanda</Link>
                                </Button>
                            </div>
                        </CardContent>
                    </Card>
                )}

                {status === "error" && (
                    <Card className="border-slate-200 shadow-sm" data-testid="verify-error">
                        <CardHeader className="pb-4">
                            <div className="flex items-center gap-2 text-destructive">
                                <AlertTriangle className="size-5" />
                                <h1 className="font-serif text-2xl font-semibold">
                                    Link non valido o scaduto
                                </h1>
                            </div>
                            <p className="text-sm text-muted-foreground">
                                {error ||
                                    "Il link di verifica non è più utilizzabile. Puoi richiederne uno nuovo qui sotto."}
                            </p>
                        </CardHeader>
                        <CardContent className="space-y-4">
                            <div className="space-y-2">
                                <label
                                    htmlFor="resendEmail"
                                    className="text-sm font-medium text-slate-700"
                                >
                                    Inserisci l'email registrata
                                </label>
                                <input
                                    id="resendEmail"
                                    type="email"
                                    value={resendEmail}
                                    onChange={(e) => setResendEmail(e.target.value)}
                                    data-testid="verify-resend-email"
                                    placeholder="tuo@email.it"
                                    className="w-full h-10 rounded-md border border-input bg-white px-3 text-sm"
                                />
                            </div>
                            <Button
                                onClick={handleResend}
                                disabled={resending || !resendEmail}
                                data-testid="verify-resend-btn"
                                className="w-full"
                            >
                                {resending && <Loader2 className="size-4 mr-2 animate-spin" />}
                                Invia una nuova email di verifica
                            </Button>
                            {resentAt && (
                                <p
                                    className="text-xs text-emerald-700 text-center"
                                    data-testid="verify-resent-info"
                                >
                                    Se l'account esiste, riceverai una nuova email.
                                </p>
                            )}
                        </CardContent>
                    </Card>
                )}

                {status === "pending" && (
                    <Card className="border-slate-200 shadow-sm" data-testid="verify-pending">
                        <CardHeader className="pb-4">
                            <div className="mx-auto size-14 rounded-2xl bg-primary/10 flex items-center justify-center">
                                <MailCheck className="size-7 text-primary" />
                            </div>
                            <h1 className="mt-4 font-serif text-2xl font-semibold text-center">
                                {emailSent
                                    ? isFresh
                                        ? "Ci siamo quasi!"
                                        : "Controlla la tua email"
                                    : "Registrazione ricevuta"}
                            </h1>
                            <p className="text-sm text-muted-foreground text-center">
                                {emailSent ? (
                                    <>
                                        Ti abbiamo inviato un'email di verifica
                                        {email ? (
                                            <>
                                                {" "}
                                                a <strong className="text-slate-800">{email}</strong>
                                            </>
                                        ) : null}
                                        . Apri la tua casella di posta e clicca sul link per
                                        attivare il tuo account.
                                        <br />
                                        <span className="text-slate-500 text-xs">
                                            Se non trovi l'email, controlla la cartella Spam o
                                            Posta indesiderata.
                                        </span>
                                    </>
                                ) : (
                                    <>
                                        Non siamo riusciti a inviare l'email di verifica in questo
                                        momento. Puoi comunque attivare subito l'account con il link
                                        qui sotto.
                                    </>
                                )}
                            </p>
                        </CardHeader>
                        <CardContent className="space-y-5">
                            {!emailSent && fallbackUrlState ? (
                                <Alert
                                    className="border-amber-300 bg-amber-50 text-amber-900"
                                    data-testid="verify-fallback-alert"
                                >
                                    <MailCheck className="size-4" />
                                    <AlertDescription>
                                        <div className="font-semibold">
                                            Non siamo riusciti a inviarti l'email
                                        </div>
                                        <div className="text-sm mt-1">
                                            Puoi comunque attivare l'account cliccando questo link
                                            (valido 48h):
                                        </div>
                                        <a
                                            href={fallbackUrlState}
                                            data-testid="verify-fallback-link"
                                            className="mt-2 block break-all text-xs font-semibold text-primary underline"
                                        >
                                            {fallbackUrlState}
                                        </a>
                                    </AlertDescription>
                                </Alert>
                            ) : (
                                <Alert className="border-primary/20 bg-primary/5 text-primary">
                                    <MailCheck className="size-4" />
                                    <AlertDescription className="text-sm text-slate-700">
                                        Non trovi l'email? Controlla anche la cartella{" "}
                                        <strong>spam</strong> o <strong>promozioni</strong>. Il link
                                        è valido per 48 ore.
                                    </AlertDescription>
                                </Alert>
                            )}

                            <div className="rounded-xl border border-slate-200 bg-slate-50 p-4">
                                <div className="text-sm font-semibold text-slate-800">
                                    Non hai ricevuto l'email?
                                </div>
                                <div className="mt-2 space-y-2">
                                    <input
                                        type="email"
                                        value={resendEmail}
                                        onChange={(e) => setResendEmail(e.target.value)}
                                        data-testid="verify-resend-email"
                                        placeholder="tuo@email.it"
                                        className="w-full h-10 rounded-md border border-input bg-white px-3 text-sm"
                                    />
                                    <Button
                                        onClick={handleResend}
                                        disabled={resending || !resendEmail}
                                        data-testid="verify-resend-btn"
                                        variant="outline"
                                        className="w-full"
                                    >
                                        {resending && (
                                            <Loader2 className="size-4 mr-2 animate-spin" />
                                        )}
                                        Reinvia email di verifica
                                    </Button>
                                    {resentAt && (
                                        <p
                                            className="text-xs text-emerald-700 text-center pt-1"
                                            data-testid="verify-resent-info"
                                        >
                                            Se l'account esiste, riceverai una nuova email.
                                        </p>
                                    )}
                                </div>
                            </div>

                            <div className="text-center text-sm text-muted-foreground pt-2">
                                Hai già verificato?{" "}
                                <Link
                                    to="/login"
                                    className="text-primary font-semibold hover:underline"
                                    data-testid="verify-to-login"
                                >
                                    Vai al login
                                </Link>
                            </div>
                        </CardContent>
                    </Card>
                )}
            </div>
        </div>
    );
}
